import { useQuery } from '@tanstack/react-query';
import { AccessControlQueryKeys } from '@/services/access-control/constants';
import { fetchMyAccess } from '@/services/access-control/access-control.service';
import { useCurrentUserStore } from '@/services/auth/store';

export type ForumAccessDeniedReason = 'base' | 'investor';

interface ForumAccessResult {
  hasAccess: boolean;
  deniedReason: ForumAccessDeniedReason | null;
  canWrite: boolean;
}

const EMPTY: ForumAccessResult = { hasAccess: false, deniedReason: 'base', canWrite: false };

export function useForumAccess() {
  const { currentUser } = useCurrentUserStore();

  const {
    data = EMPTY,
    isLoading,
    isError,
  } = useQuery({
    queryKey: [AccessControlQueryKeys.MY_ACCESS],
    queryFn: fetchMyAccess,
    select: (data): ForumAccessResult => {
      const hasAccess = data.effectivePermissions.includes('forum.read');
      const canWrite = data.effectivePermissions.includes('forum.write');
      if (hasAccess) return { hasAccess: true, deniedReason: null, canWrite };
      const hasMemberAccess = data.effectivePermissions.includes('member.contacts.read');
      return { hasAccess: false, deniedReason: hasMemberAccess ? 'investor' : 'base', canWrite };
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!currentUser,
    retry: 2,
  });

  return { hasAccess: data.hasAccess, deniedReason: data.deniedReason, isLoading, isError, canWrite: data.canWrite };
}
